import { useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { uniqueArray } from '../../utils';
import {
  Bubble,
  Button,
  CheckChangeContext,
  Checkbox,
  Icon,
  Input,
  List,
  Radio,
  StatusTip,
  Tag,
} from 'tea-component';
import React from 'react';
import BubbleCommon from '../../../BubbleCommon';
import { escapeAllTags, keywordRenderFunc } from '@/pages/opinion/utils/text';
import { NO_DATE } from '@/pages/opinion/constants';
import './index.scss';

type SearchSelectedElementType = {
  changeInputRef: (value: any) => void;
  tagList: Array<string>;
  getFilterList: any;
  loading: boolean;
  basicOptions: any;
  setLoading: any;
  onFocus: any;
  setTagList: any;
  setKeyword?: React.Dispatch<React.SetStateAction<string>>;
  allFilterTips?: any;
  loadingText?: any;
  placeholderText?: any;
  topTips?: any;
  mainText?: any;
  isStringOptions?: boolean;
  footerElement?: (value: { searchValue: string; className: string }) => any;
  hasAllBtn?: boolean;
  hasBubble?: boolean;
};

export default function SearchSelectedElement({
  changeInputRef,
  tagList = [],
  getFilterList,
  loading,
  basicOptions,
  setLoading,
  onFocus,
  setTagList,
  setKeyword,
  allFilterTips = null,
  loadingText = NO_DATE.EMPTY,
  placeholderText = NO_DATE.EMPTY,
  topTips = NO_DATE.EMPTY,
  mainText = NO_DATE.EMPTY,
  isStringOptions = true,
  footerElement,
  hasAllBtn = true,
  hasBubble = false,
}: SearchSelectedElementType) {
  const [value, setValue] = useState('');
  const { formatMessage } = useIntl();
  const [options, optionsValue] = useMemo(() => {
    let basicReviewers = basicOptions || [];
    if (isStringOptions) {
      const options = basicReviewers.map((item: string) => ({
        text: item,
        value: item,
      }));

      return [options, basicReviewers];
    }
    const optionsValue = basicReviewers.map((item: any) => item.value);
    return [basicReviewers, optionsValue];
  }, [basicOptions]);

  const status = useMemo(() => {
    if (loading) {
      return 'loading';
    }
    if (options.length === 0) {
      return 'empty';
    }

    return false;
  }, [loading, options]);

  const isAllSelected = useMemo(() => {
    return options.every((item: { value: string }) =>
      tagList.includes(item.value),
    );
  }, [tagList, options]);

  function onFocusInput() {
    setTimeout(onFocus, 10);
  }

  const changeAllSelected = () => {
    if (isAllSelected) {
      setTagList(tagList.filter((item: any) => !optionsValue.includes(item)));
      if (!value) getFilterList();
    } else {
      const func = getReplaceTextFunc(value);
      optionsValue.forEach((item: string) => func(item));
      setTagList(uniqueArray([...tagList, ...optionsValue]));
      onFocusInput();
    }
  };

  const getReplaceTextFunc = (originText: string) => {
    return (selectedText: string) => {
      const replaceText = originText.startsWith(selectedText)
        ? `${selectedText},`
        : `,${selectedText}`;
      originText = originText
        .replace(replaceText, '')
        .replace(selectedText, '');
      setValue(originText);
    };
  };

  const changeSelected = (
    valueList: Array<string>,
    context: CheckChangeContext,
  ) => {
    const selectedText = context.check.name;
    if (selectedText && value.includes(selectedText)) {
      getReplaceTextFunc(value)(selectedText);
    }
    setTagList(valueList);
    onFocusInput();
  };

  const deleteTag = (tag: string) => {
    const list = tagList.filter((item: any) => item !== tag);
    if (list.length === 0) getFilterList();

    setTagList(list);
  };
  return (
    <div className="search-selected-element-wrapper">
      <div className="dbo-search-input-box">
        <div className="dbo-search-input-info">
          {tagList.map((item: string) => (
            <Tag
              key={item}
              className="dbo-tag-item"
              theme="default"
              onClose={() => deleteTag(item)}
            >
              {item}
            </Tag>
          ))}
          <Input
            className={`dbo-search-input`}
            value={value}
            ref={changeInputRef}
            placeholder={formatMessage({
              id: placeholderText,
            })}
            onChange={(text) => {
              setValue(text);
              setLoading(true);
              getFilterList(text);
              setKeyword?.(value);
            }}
          />
        </div>
        <div className="dbo-select-search-icon">
          <Icon
            type="dismiss"
            className={`icon-info before ${tagList.length > 0 || value ? 'show' : 'none'
              }`}
            onClick={() => {
              setValue('');
              setTagList([]);
              getFilterList();
            }}
          />
          <Icon
            type="search"
            className="icon-info"
            onClick={() => getFilterList(value)}
          />
        </div>
      </div>

      {status ? (
        <StatusTip
          className="loading-text"
          loadingText={formatMessage({ id: loadingText })}
          status={status}
        />
      ) : (
        <>
          <div className="top-tips">{formatMessage({ id: topTips })}</div>
          {!value && (
            <div className="radio-all-box">
              <Radio
                name="all"
                className="radio-item"
                onClick={() => setTagList([])}
                value={tagList.length === 0}
              >
                {formatMessage({ id: 'All' })}
                {allFilterTips && (
                  <BubbleCommon
                    placement="top"
                    tipsMap={allFilterTips}
                  ></BubbleCommon>
                )}
              </Radio>
            </div>
          )}
          <div className="title-box">
            <label className="label-title">
              {value
                ? `${formatMessage({ id: 'Search Result' })} (${options.length
                })`
                : formatMessage({ id: mainText })}
            </label>
            {hasAllBtn && (
              <Button onClick={changeAllSelected} type="link">
                {formatMessage({
                  id: isAllSelected ? 'Clear All' : 'Select All',
                })}
              </Button>
            )}
          </div>
          <div className="dbo-list-box">
            <Checkbox.Group
              className="checkbox-group-box"
              value={tagList}
              onChange={changeSelected}
            >
              <List type="option" className="select-list-box">
                {options.map(
                  (item: {
                    text: string;
                    value: string;
                    disabled: boolean;
                  }) => {
                    const renderText = keywordRenderFunc({
                      text: escapeAllTags(item.text),
                      keyword: value?.split(',')?.filter(Boolean) || [],
                    });

                    return (
                      <List.Item key={item.value} className="select-list-item">
                        <Checkbox
                          disabled={item.disabled}
                          className="check-box-item"
                          name={item.value}
                        >
                          {hasBubble ? (
                            <Bubble content={item.text}>{renderText}</Bubble>
                          ) : (
                            renderText
                          )}
                        </Checkbox>
                      </List.Item>
                    );
                  },
                )}
              </List>
            </Checkbox.Group>
          </div>
        </>
      )}

      {status !== 'loading' &&
        footerElement?.({
          searchValue: value,
          className: 'dbo-footer-element-tips',
        })}
    </div>
  );
}
